import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import SearchableSelect from '../../../componants/searchableSelect/SearchableSelect'
import './reviewRating.css'

// Matches the recordModule values already used for SaveToWishlistButton
// across the app (bus/train have no such module yet, so left out here too).
const SERVICE_OPTIONS = [
    { value: 'flight', label: 'Flight' },
    { value: 'hotel', label: 'Hotel' },
    { value: 'cruise', label: 'Cruise' },
    { value: 'car', label: 'Car Rental' },
    { value: 'venue', label: 'Venue' },
    { value: 'event', label: 'Event' },
]

// Bigger star used for the interactive rating inputs — same shape, sized up
// to be easy to click/tap.
const RatingStarInput = ({ value, onChange }) => {
    const [hovered, setHovered] = useState(0)
    return (
        <div className='reviewFormStars' onMouseLeave={() => setHovered(0)}>
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    type='button'
                    key={star}
                    className='reviewFormStarBtn'
                    onMouseEnter={() => setHovered(star)}
                    onClick={() => onChange(star)}
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                >
                    <svg width="24" height="24" viewBox="0 0 14 14" fill="none">
                        <path d="M7 10.65 2.89 12.95 3.8 8.33.34 5.13l4.68-.55L7 .29l1.98 4.29 4.68.55-3.46 3.2.92 4.62Z" fill={star <= (hovered || value) ? '#F5A623' : '#E5E7EB'} />
                    </svg>
                </button>
            ))}
        </div>
    )
}

const REVIEW_CATEGORIES = [
    { key: 'cleanliness', label: 'Cleanliness' },
    { key: 'service', label: 'Service' },
    { key: 'location', label: 'Location' },
    { key: 'value_for_money', label: 'Value for Money' },
]

// The 4 category star fields only make sense for a place/experience you can
// rate on cleanliness/location etc. — hidden for flight/cruise/car reviews.
const CATEGORY_SERVICE_TYPES = ['hotel', 'venue', 'event']

// "self" is the documented fallback provider value for bookings that have no
// third-party vendor (or when arriving without a specific booking at all).
const EMPTY_FORM = {
    serviceType: '', objectId: '', title: '', provider: 'self',
    overall: 0, cleanliness: 0, service: 0, location: 0, value_for_money: 0, text: '',
}

const ReviewRating = () => {
    const location = useLocation()
    const navigate = useNavigate()
    // Arriving from a specific booking's "Rate" action (My Trips) — the
    // service/booking id are locked to that booking, not user-editable, so a
    // review can only ever be left against the booking it was opened from.
    const prefill = location.state?.prefill || null
    // Provider isn't something the user picks — /users/trips already tells us
    // the real vendor (prefill.provider, e.g. "trawex", or "self" when the
    // booking has none), sent silently in the payload with no field shown.
    const [form, setForm] = useState(() => (prefill ? { ...EMPTY_FORM, ...prefill, provider: prefill.provider || 'self' } : EMPTY_FORM))
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    // Captured once at mount — the effect below clears `prefill` from history
    // state right after mounting (so a later sidebar revisit doesn't re-lock
    // the form to a stale booking), which would otherwise make `!!prefill`
    // flip to false on the very next render and silently un-disable these
    // fields while still viewing the same locked-to-a-booking session.
    const [locked] = useState(() => !!prefill)

    // This component remounts every time the Profile sidebar switches away
    // and back to this tab — without clearing it, a stale prefill would keep
    // re-locking the form to whatever booking was last rated, even after the
    // user navigates elsewhere and returns via the sidebar (not via "Rate").
    useEffect(() => {
        if (prefill) {
            navigate(location.pathname, { replace: true, state: { section: 'rating-reviews' } })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setCategoryRating = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

    const handleSubmitReview = () => {
        if (submitting) return
        setSubmitAttempted(true)
        if (!form.serviceType) {
            showToast('Please select what you are rating', 'error')
            return
        }
        if (!form.objectId.trim()) {
            showToast('Please enter the booking/property ID', 'error')
            return
        }
        if (!form.title.trim()) {
            showToast('Please enter a review title', 'error')
            return
        }
        if (!form.overall) {
            showToast('Please select an overall rating', 'error')
            return
        }
        // Backend requires cleanliness/service_rate/location/value_for_money to
        // each be >= 1 for these service types (confirmed via a live 400:
        // "Service Rate: Ensure this value is greater than or equal to 1."
        // when a sub-category star was left unclicked) — enforce the same here.
        if (CATEGORY_SERVICE_TYPES.includes(form.serviceType) && REVIEW_CATEGORIES.some((category) => !form[category.key])) {
            showToast('Please rate all categories', 'error')
            return
        }
        if (!form.text.trim()) {
            showToast('Please write your review', 'error')
            return
        }

        setSubmitting(true)
        service
            .post(endpoints.bookings.rating, {
                service: form.serviceType,
                object_id: Number(form.objectId),
                title: form.title,
                description: form.text,
                rating: form.overall,
                provider: form.provider,
                cleanliness: form.cleanliness,
                service_rate: form.service,
                location: form.location,
                value_for_money: form.value_for_money,
            })
            .then(() => {
                // service.js already toasts the backend's success message (not silent here) —
                // back to whichever trip list this service belongs to — 'events'/'venues'
                // have their own dedicated My Trips tabs, everything else (hotel/flight/
                // cruise/car) lives under the generic 'trips' tab (mytrips.jsx).
                const section = form.serviceType === 'event' ? 'events' : form.serviceType === 'venue' ? 'venues' : 'trips'
                navigate('/profile', { state: { section } })
            })
            .finally(() => setSubmitting(false))
    }

    return (
        <div className='reviewPart bgWhite commonBorder borderRadius20 paddingCommon20'>
            <div className='reviewPart__head'>
                <div className='bsSectionTitle'>Rating & Reviews</div>
            </div>

            <div className='reviewForm bgWhite commonBorder borderRadius20'>
                <div className='reviewForm__title'>Write a Review</div>

                <div className='reviewForm__categoryGrid'>
                    <div className='reviewForm__field'>
                        <label>What are you rating? {submitAttempted && !form.serviceType ? <span className='reviewForm__required'>*</span> : ''}</label>
                        <SearchableSelect
                            options={SERVICE_OPTIONS}
                            value={form.serviceType}
                            onChange={(value) => setForm((prev) => ({ ...prev, serviceType: value }))}
                            placeholder='Select'
                            disabled={locked}
                            className={submitAttempted && !form.serviceType ? 'reviewForm__input--error' : ''}
                        />
                    </div>
                    <div className='reviewForm__field'>
                        <label>Booking / Property ID {submitAttempted && !form.objectId.trim() ? <span className='reviewForm__required'>*</span> : ''}</label>
                        <input
                            type='text'
                            inputMode='numeric'
                            disabled={locked}
                            className={submitAttempted && !form.objectId.trim() ? 'reviewForm__input--error' : ''}
                            value={form.objectId}
                            onChange={(e) => setForm((prev) => ({ ...prev, objectId: e.target.value.replace(/\D/g, '') }))}
                        />
                    </div>
                </div>

                <div className='reviewForm__field'>
                    <label>Review Title {submitAttempted && !form.title.trim() ? <span className='reviewForm__required'>*</span> : ''}</label>
                    <input
                        type='text'
                        placeholder='Sum up your experience'
                        className={submitAttempted && !form.title.trim() ? 'reviewForm__input--error' : ''}
                        value={form.title}
                        onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    />
                </div>

                <div className='reviewForm__field'>
                    <label>Overall Rating {submitAttempted && !form.overall ? <span className='reviewForm__required'>*</span> : ''}</label>
                    <RatingStarInput value={form.overall} onChange={(value) => setCategoryRating('overall', value)} />
                </div>

                {CATEGORY_SERVICE_TYPES.includes(form.serviceType) && (
                    <div className='reviewForm__categoryGrid'>
                        {REVIEW_CATEGORIES.map((category) => (
                            <div className='reviewForm__field' key={category.key}>
                                <label>{category.label} {submitAttempted && !form[category.key] ? <span className='reviewForm__required'>*</span> : ''}</label>
                                <RatingStarInput value={form[category.key]} onChange={(value) => setCategoryRating(category.key, value)} />
                            </div>
                        ))}
                    </div>
                )}

                <div className='reviewForm__field'>
                    <label>Your Review {submitAttempted && !form.text.trim() ? <span className='reviewForm__required'>*</span> : ''}</label>
                    <textarea
                        rows={4}
                        placeholder='Share details of your experience...'
                        className={submitAttempted && !form.text.trim() ? 'reviewForm__input--error' : ''}
                        value={form.text}
                        onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
                    />
                </div>

                <button type='button' className='commonBtn reviewForm__submitBtn' onClick={handleSubmitReview} disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Review'}
                </button>
            </div>
        </div>
    )
}

export default ReviewRating
